import { useMemo } from 'react';
import { Location } from '@lingoda/monolith-graphql';
import { RadioGroup } from '@lingoda/forms';
import { Box, Grid, Paper, Stack, Typography } from '@lingoda/ui';
import { colorsPalette, styled } from '@lingoda/ui/theme';
import {
    ContentBlockTypography,
    findAllContentBlocks,
    findContentBlock,
} from '@lingoda/content-blocks';
import {
    CheckoutButton,
    CheckoutSkipTrialButton,
    CheckoutTrialButton,
    TrialDetails,
} from '../../../components';
import type { PackageLayoutProps } from '../types';
import type { PackageActionProps, PurchasePackage } from '../../../types';
import type { PropsWithChildren } from 'react';

const ListWrapper = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    flex: 5,

    '& .MuiFormControl-root': {
        display: 'flex',
        width: '100%',
    },
}));

const DesktopListLayout = ({
    children,
    selectedPackage,
    onCheckout,
    isTrialAvailable,
    footerMessage,
    isLearningLanguageChange,
}: PropsWithChildren<PackageLayoutProps & PackageActionProps>) => (
    <Paper variant="outlined">
        <Box p={4}>
            <Grid container>
                <ListWrapper>
                    <RadioGroup name="purchasePackageId" value={selectedPackage?.id || null}>
                        {children}
                    </RadioGroup>
                    {footerMessage && (
                        <Typography variant="body1" htmlColor={colorsPalette.blackAlpha[60]} mt={2}>
                            {footerMessage}
                        </Typography>
                    )}
                </ListWrapper>

                {selectedPackage && (
                    <SelectedDetails
                        pkg={selectedPackage}
                        isTrialAvailable={isTrialAvailable}
                        onCheckout={onCheckout}
                        isLearningLanguageChange={isLearningLanguageChange}
                    />
                )}
            </Grid>
        </Box>
    </Paper>
);

interface SelectedDetailsProps extends PackageActionProps {
    pkg: PurchasePackage;
    isTrialAvailable?: boolean;
    isLearningLanguageChange?: boolean;
}

const SelectedDetails = ({ pkg, isTrialAvailable, onCheckout }: SelectedDetailsProps) => {
    const contentBlocks = useMemo(
        () => ({
            title: findContentBlock(pkg.contentBlocks, Location.SummaryTitle),
            content: findAllContentBlocks(pkg.contentBlocks, Location.SummaryContent),
            trialDetails: findContentBlock(pkg.contentBlocks, Location.SummaryTrialDetails),
        }),
        [pkg.contentBlocks],
    );

    return (
        <Grid item flex={3}>
            <Stack spacing={3}>
                <Stack spacing={4}>
                    {contentBlocks.title && (
                        <ContentBlockTypography
                            {...contentBlocks.title}
                            variant="h3"
                            textColor="text.primary"
                        />
                    )}

                    {contentBlocks.content && (
                        <Stack spacing={2}>
                            {contentBlocks.content.map((item, index) => (
                                <ContentBlockTypography
                                    key={index}
                                    {...item}
                                    variant="body1"
                                    textColor="text.primary"
                                    iconColor="primary.main"
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>

                {!pkg.isCurrent &&
                    (isTrialAvailable ? (
                        <Stack spacing={2}>
                            {contentBlocks.trialDetails && (
                                <Box textAlign="center" mt={2}>
                                    <TrialDetails>{contentBlocks.trialDetails.text}</TrialDetails>
                                </Box>
                            )}
                            <CheckoutTrialButton
                                fullWidth
                                onClick={() => onCheckout({ trial: true })}
                            />
                            <CheckoutSkipTrialButton
                                fullWidth
                                onClick={() => onCheckout({ trial: false })}
                            />
                        </Stack>
                    ) : (
                        <Box pt={1}>
                            <CheckoutButton
                                fullWidth
                                onClick={() => onCheckout({ trial: false })}
                            />
                        </Box>
                    ))}
            </Stack>
        </Grid>
    );
};

export default DesktopListLayout;
